<template>
  <li v-if="isLessonStartCondition" class="game-condition-item">
    <IntroductionIcon />
    <span>{{ t("gamification_game_condition_lesson_starts_label") }}</span>
    <cv-button kind="ghost" class="icon-button btn-delete-condition">
      <svg-icon name="Trash24" kind="action" @click="deleteCondition" />
    </cv-button>
  </li>
  <component
    :is="component"
    v-else
    :key="componentKey"
    :condition="condition"
    :sections="sectionList"
    :quizzes="quizzes"
    :game-awards="gameAwards"
    :validation-errors="validationErrors"
    :target-game-award="targetGameAward"
    :in-edit="inEdit"
    :hide-section-activity-number="hideSectionActivityNumber"
    :is-from-game-story="isFromGameStory"
    :disable-fields="!gameData.enabled"
    @delete="deleteCondition"
    @change="handleEventChange"
  />
</template>

<script setup lang="ts">
import { CONDITION_TYPE, GAME_AWARD_TYPE } from "@modules/editor/service/gamification.constant";
import GamificationConditionLevelItem from "@modules/editor/components/gamification/GamificationConditionLevelItem";
import GamificationConditionGameAwardItem from "@modules/editor/components/gamification/GamificationConditionGameAwardItem";
import GamificationConditionGameChoiceItem from "@modules/editor/components/gamification/GamificationConditionGameChoiceItem";
import GamificationConditionQuizRankItem from "@modules/editor/components/gamification/GamificationConditionQuizRankItem";
import { isNull } from "@utils/var.check";
import IntroductionIcon from "@images/icons/indicative/32px/Introduction32.svg?component";
import { ACTIVITY_OR_ACTIVITY_QUIZ_CRITERION_PREREQUISITE } from "@modules/rule-condition/service/rule.condition.constant";
import { ref, computed, watch, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const emit = defineEmits(["change", "delete"]);

// Props
interface Props {
  condition: object;
  targetGameAward?: object;
  sections?: unknown[] | null;
  quizzes?: unknown[] | null;
  gameData?: object;
  validationErrors?: object;
  inEdit?: boolean;
  hideSectionActivityNumber: boolean;
  isFromGameStory?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  sections: null,
  quizzes: null,
  gameData: () => ({}),
  validationErrors: () => ({}),
  inEdit: false,
  isFromGameStory: false,
});

// Data
const component = ref(null);
const gameAwards = ref([]);
const componentKey = ref(0);

// Computed
const sectionList = computed(() => {
  if (isNull(props.sections)) {
    return [];
  }
  const sectionList = JSON.parse(JSON.stringify(props.sections));
  if (props.condition.type === CONDITION_TYPE.RESOURCE_COMPLETE) {
    //remove course cover for completion conditions
    sectionList.shift();
  }
  return sectionList;
});

const isLessonStartCondition = computed(() => {
  const { resourceCompletionPrerequisite, criterion } = props.condition;
  return (
    resourceCompletionPrerequisite === -1 &&
    criterion === ACTIVITY_OR_ACTIVITY_QUIZ_CRITERION_PREREQUISITE.RESOURCE_START
  );
});

// Methods
const handleEventChange = function (data) {
  emit("change", data);
};

const deleteCondition = function () {
  emit("delete", props.condition.id);
};

const updateComponent = function (type) {
  if (type === CONDITION_TYPE.GAME_AWARD) {
    component.value = GamificationConditionGameAwardItem;
    gameAwards.value =
      props.condition.gameAwardType === GAME_AWARD_TYPE.GAME_STORY
        ? props.gameData.gameStories
        : [
            ...props.gameData.achievements,
            ...props.gameData.gameAvatars,
            ...props.gameData.digitalBadges,
          ];
  } else if (type === CONDITION_TYPE.GAME_CHOICE) {
    component.value = GamificationConditionGameChoiceItem;
    gameAwards.value = props.gameData.gameStories.concat(
      props.gameData.achievements,
      props.gameData.gameAvatars,
    );
  } else if (type === CONDITION_TYPE.LEVEL_UP) {
    component.value = GamificationConditionLevelItem;
  } else if (type === CONDITION_TYPE.QUIZ_RANK) {
    component.value = GamificationConditionQuizRankItem;
  }
};

// Watch
watch(
  () => props.gameData?.gameStories,
  () => {
    updateComponent(props.condition.type);
  },
  { deep: true },
);

watch(
  () => props.gameData?.achievements,
  () => {
    updateComponent(props.condition.type);
  },
  { deep: true },
);

watch(
  () => props.gameData?.gameAvatars,
  () => {
    updateComponent(props.condition.type);
  },
  { deep: true },
);

watch(
  () => props.condition.type,
  (type) => {
    updateComponent(type);
  },
);

onBeforeMount(() => {
  // Created
  const type = props.condition.criterion;
  updateComponent(type);
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.game-condition-item {
  > .bx--form-item {
    margin-right: $spacing-05;
    min-height: 2.5rem;

    &:not(.cv-number-input) {
      @include media("mobile") {
        flex: 0 0 12rem;
        max-width: 12rem;
      }

      @include media("tablet") {
        flex: 0 0 16rem;
        max-width: 16rem;
      }

      @include media("desktop") {
        flex: 0 0 20rem;
        max-width: 20rem;
      }
    }

    &.cv-number-input {
      flex: 0 1 auto;
      margin: 0 $spacing-03;

      @include media("mobile") {
        // Remove left margin when it flows to the 2nd line in mobile
        // as the margin will be added for the span
        margin-left: 0;
      }
    }

    .bx--number__input-wrapper {
      > input {
        min-width: 5rem;
      }
    }
  }

  &.choice {
    .bx--list-box__label {
      display: flex;
      align-items: center;
    }
    .game-choice-dropdown {
      .bx--list-box__label,
      .bx--dropdown-link {
        .icon {
          max-height: 32px;
          margin-right: $spacing-03;
        }
      }
    }
  }

  .btn-delete-condition {
    @include media("tablet-min") {
      margin-left: auto;
    }
  }

  .ellipsis-text {
    @include carbon--type-style("productive-heading-02");
    color: $text-02;
  }
}
</style>
